import {
  settlementService,
  type SettlementPharmaInfoResponse,
  type SettlementUserSearchRequest,
} from '@/entities/settlement/api/settlement.api';
import type { Pageable } from '@/entities/user/api/user.api';
import { usePaginatedQuery } from '@/shared/hooks/usePaginatedQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const settlementKeys = {
  all: ['settlements'] as const,
  pharmaLists: () => [...settlementKeys.all, 'pharma-list'] as const,
  pharmaList: (yearMonth: string, pharmaCompanyId: number | undefined, pageable: Pageable) =>
    [...settlementKeys.pharmaLists(), yearMonth, pharmaCompanyId, pageable] as const,
  userLists: () => [...settlementKeys.all, 'user-list'] as const,
  userList: (search: SettlementUserSearchRequest) =>
    [...settlementKeys.userLists(), search] as const,
  status: (yearMonth: string) =>
    [...settlementKeys.all, 'status', yearMonth] as const,
  pharmaDetail: (
    settlementYearMonth: string,
    prescriptionYearMonth: string,
    pharmaCompanyId: number,
  ) =>
    [
      ...settlementKeys.all,
      'pharma-detail',
      settlementYearMonth,
      prescriptionYearMonth,
      pharmaCompanyId,
    ] as const,
  pharmaDrugs: (
    settlementYearMonth: string,
    prescriptionYearMonth: string,
    pharmaCompanyId: number,
  ) =>
    [
      ...settlementKeys.all,
      'pharma-drugs',
      settlementYearMonth,
      prescriptionYearMonth,
      pharmaCompanyId,
    ] as const,
  userDetails: (
    userEmail: string,
    prescriptionYearMonth: string,
    settlementYearMonth: string,
  ) =>
    [
      ...settlementKeys.all,
      'user-details',
      userEmail,
      prescriptionYearMonth,
      settlementYearMonth,
    ] as const,
  pharmaCompanies: (yearMonth: string) =>
    [...settlementKeys.all, 'pharma-companies', yearMonth] as const,
  months: () => [...settlementKeys.all, 'months'] as const,
};

export interface SettlementPharmaListParams {
  yearMonth: string;
  pharmaCompanyId?: number;
  pageable: Pageable;
}

/** 제약사별 정산 업로드 목록 — 레거시 `settlementUpload.js` */
export const useSettlementPharmaList = ({
  yearMonth,
  pharmaCompanyId,
  pageable,
}: SettlementPharmaListParams) =>
  usePaginatedQuery<{ yearMonth: string; pharmaCompanyId?: number }, SettlementPharmaInfoResponse>({
    queryKeyPrefix: settlementKeys.pharmaLists(),
    search: { yearMonth, pharmaCompanyId },
    pageable,
    queryFn: async ({ search: s, pageable: p }) =>
      settlementService.getSettlementPharma(s.yearMonth, p, s.pharmaCompanyId),
  });

/** 사용자별 처방 정산 목록 — 레거시 `prescriptionSettlements.js` */
export const useUserSettlementList = (search: SettlementUserSearchRequest) =>
  useQuery({
    queryKey: settlementKeys.userList(search),
    queryFn: () => settlementService.getUserSettlementInfos(search),
    enabled: !!search.settlementYearMonth,
  });

/** 제약사별 정산 목록 */
export const usePharmaSettlementList = (
  search: SettlementUserSearchRequest & { pharmaName?: string },
) =>
  useQuery({
    queryKey: [...settlementKeys.userLists(), 'pharma', search],
    queryFn: () => settlementService.getPharmaSettlementInfos(search),
    enabled: !!search.settlementYearMonth,
  });

/** 병의원별 정산 목록 */
export const useClinicSettlementList = (
  search: SettlementUserSearchRequest & { clinicName?: string },
) =>
  useQuery({
    queryKey: [...settlementKeys.userLists(), 'clinic', search],
    queryFn: () => settlementService.getClinicSettlementInfos(search),
    enabled: !!search.settlementYearMonth,
  });

export const useSettlementStatus = (settlementYearMonth: string | null) =>
  useQuery({
    queryKey: settlementYearMonth
      ? settlementKeys.status(settlementYearMonth)
      : [],
    queryFn: () => {
      if (!settlementYearMonth) throw new Error('yearMonth required');
      return settlementService.getSettlementStatus(settlementYearMonth);
    },
    enabled: !!settlementYearMonth,
  });

export const useSettlementPharmaDetail = (
  settlementYearMonth: string | null,
  prescriptionYearMonth: string | null,
  pharmaCompanyId: number | null,
) =>
  useQuery({
    queryKey:
      settlementYearMonth && prescriptionYearMonth && pharmaCompanyId != null
        ? settlementKeys.pharmaDetail(
            settlementYearMonth,
            prescriptionYearMonth,
            pharmaCompanyId,
          )
        : [],
    queryFn: async () => {
      if (
        !settlementYearMonth ||
        !prescriptionYearMonth ||
        pharmaCompanyId == null
      ) {
        throw new Error('params required');
      }
      return settlementService.getSettlementPharmaDetail(
        settlementYearMonth,
        prescriptionYearMonth,
        pharmaCompanyId,
      );
    },
    enabled:
      !!settlementYearMonth &&
      !!prescriptionYearMonth &&
      pharmaCompanyId != null,
  });

export const useSettlementPharmaDrugs = (
  settlementYearMonth: string | null,
  prescriptionYearMonth: string | null,
  pharmaCompanyId: number | null,
) =>
  useQuery({
    queryKey:
      settlementYearMonth && prescriptionYearMonth && pharmaCompanyId != null
        ? settlementKeys.pharmaDrugs(
            settlementYearMonth,
            prescriptionYearMonth,
            pharmaCompanyId,
          )
        : [],
    queryFn: async () => {
      if (
        !settlementYearMonth ||
        !prescriptionYearMonth ||
        pharmaCompanyId == null
      ) {
        throw new Error('params required');
      }
      return settlementService.getSettlementDrugs(
        settlementYearMonth,
        prescriptionYearMonth,
        pharmaCompanyId,
      );
    },
    enabled:
      !!settlementYearMonth &&
      !!prescriptionYearMonth &&
      pharmaCompanyId != null,
  });

export const useUserSettlementDetails = (
  userEmail: string | null,
  prescriptionYearMonth: string | null,
  settlementYearMonth: string | null,
) =>
  useQuery({
    queryKey:
      userEmail && prescriptionYearMonth && settlementYearMonth
        ? settlementKeys.userDetails(
            userEmail,
            prescriptionYearMonth,
            settlementYearMonth,
          )
        : [],
    queryFn: async () => {
      if (!userEmail || !prescriptionYearMonth || !settlementYearMonth) {
        throw new Error('params required');
      }
      return settlementService.getUserSettlementDetails(
        prescriptionYearMonth,
        settlementYearMonth,
        userEmail,
      );
    },
    enabled: !!userEmail && !!prescriptionYearMonth && !!settlementYearMonth,
  });

export const usePharmaCompaniesForSettlement = (yearMonth: string | null) =>
  useQuery({
    queryKey: yearMonth ? settlementKeys.pharmaCompanies(yearMonth) : [],
    queryFn: async () => {
      if (!yearMonth) throw new Error('yearMonth required');
      return settlementService.getPharmaCompanies(yearMonth);
    },
    enabled: !!yearMonth,
  });

/** 정산 조회 가능 월 목록 — 레거시 `getListOfSettlementMonths` */
export const useSettlementMonths = () =>
  useQuery({
    queryKey: settlementKeys.months(),
    queryFn: () => settlementService.getSettlementMonths(),
  });

export const useGenerateSettlement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settlementYearMonth: string) =>
      settlementService.generateSettlementData({ settlementYearMonth }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
};

export const useMergePharmaSettlement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settlementYearMonth: string) =>
      settlementService.mergePharmaSettlementData({ settlementYearMonth }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
};

export const usePublishSettlement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settlementYearMonth: string) =>
      settlementService.publishSettlement({ settlementYearMonth }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
};

export const useUpdateSettlementPharmaStatuses = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: settlementService.updateSettlementPharmaStatuses,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
};

export const useDeleteSettlementPharmaDrugs = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: settlementService.deletePharmaSettlements,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
};

export const useDeleteUserSettlement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: settlementService.deleteUserSettlement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
};

/** 제약사 정산 CSV 업로드 — 레거시 `uploadSettlementData` */
export const useUploadPharmaSettlements = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: settlementService.uploadPharmaSettlements,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
};

/** 처방 정산 상세 export — 레거시 `handleDownloadSettlementUserDetails` */
export const useExportUserSettlements = () =>
  useMutation({
    mutationFn: (params: { settlementYearMonth: string; email?: string }) =>
      settlementService.exportUserSettlements(params),
  });
